// fleetBoard.js
// -----------------------------------------------------------------------------
// One row per truck for กระดานติดตามรถ: how far through the day it is, how late
// it is, and how fresh the last position fix is.
//
// PURE: no Vue, no Firestore, and no clock of its own -- `now` is an argument,
// which is what makes every assertion in scripts/check-fleet-board.js
// deterministic.
//
// IT DOES NOT PLAN. Grouping and the ETA chain come from driverDay, the same
// functions the driver's own screen calls. A second planner here would drift
// from the driver's, and the two would disagree about the same truck with
// nothing to show which was wrong.
// -----------------------------------------------------------------------------

const D = require('./driverDay')

// Measured against promisedTime -- the time the office told the CUSTOMER, not
// the app's own plan. The plan is a guess; the promise is what a shop will
// phone about.
const ON_TIME_MINUTES = 15
const LATE_MINUTES = 45

// Ping age buckets. Matched to MIN_PING_AGE_MS (10 minutes) in driverService:
// a fix arrives at most every 10 minutes when everything is working, so 15 is
// the first age that means something is not.
const FRESH_MS = 15 * 60 * 1000
const STALE_MS = 30 * 60 * 1000

// 'HH:mm' -> minutes since midnight. Returns null rather than 0 for anything
// unparseable: 0 is a real time (midnight) and a delay computed against a
// silently-zeroed promise would read as "11 hours late" on every row.
function toMinutes (clock) {
  if (typeof clock !== 'string') return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(clock.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

/**
 * The minute a promise runs out -- driverDay's window reading, not a second one.
 *
 * THE PROMISE IS A WINDOW, NOT AN INSTANT. What lands in a deliveryList row's
 * `timeUpdate` is whatever carBooking.vue's _prepareTime() returned: a range
 * ('08:00 - 12:00'), a slot ('AM', 'ทั้งวัน'), or occasionally a bare clock. It
 * is almost never the 'HH:mm' this module first insisted on, which is why every
 * truck on the board once read ไม่ทราบเวลา -- the delay column, the whole point
 * of the screen, was dead against real data.
 *
 * THE END of the window is the promise. A job booked 08:00-12:00 and delivered
 * at 11:30 is on time; measuring from the start would report it 210 minutes late
 * on every ทั้งวัน slot and train staff to ignore the colour.
 *
 * Read through driverDay for the same reason the ETA chain is: one reading of a
 * promised time in this app, not two that can disagree about the same row.
 */
function promisedEndMinutes (promised) {
  return D.promisedWindow(promised).end
}

// A real instant -> minutes since Bangkok midnight, on the same scale as the
// 'HH:mm' a promise is written in.
//
// THROUGH driverDay, NOT through the Date's local-time getters. Those getters
// read whatever zone the browser is set to, and a machine on UTC would put a
// 13:40 Bangkok arrival at 06:40 -- reporting a stop promised 13:00 as six
// hours EARLY, in green. The same reading anchors `now`, so a mis-zoned browser
// would also silently disarm the floor under an un-reached stop's ETA and bring
// back the stuck-truck-reads-green bug this module exists to prevent.
// driverDay.minutesFromISO is fixed at +07:00 -- Thailand has never observed
// DST -- and is already the function every atDevice stamp on this board goes
// through, so routing here keeps one reading of the clock, not two.
function minutesFromDate (value) {
  if (!(value instanceof Date)) return null
  if (isNaN(value.getTime())) return null
  return D.minutesFromISO(value.toISOString())
}

function delayLevelFor (minutes) {
  if (minutes == null) return 'unknown'
  if (minutes <= ON_TIME_MINUTES) return 'onTime'
  if (minutes <= LATE_MINUTES) return 'late'
  return 'veryLate'
}

function freshnessFor (ageMs) {
  if (ageMs == null) return 'none'
  if (ageMs <= FRESH_MS) return 'fresh'
  if (ageMs <= STALE_MS) return 'aging'
  return 'stale'
}

// Minutes late for one stop, signed: positive is late, negative is early.
//
// A stop the driver has REACHED is measured from what actually happened.
// Everything else is measured from planStops' prediction, floored at `now`.
// The plan is never used where the truth exists, and no number is invented
// where neither does.
//
// `nowMinutes` is minutes since midnight, passed in from buildBoard's `now`.
// It is never read from a clock here -- that is what keeps every assertion in
// scripts/check-fleet-board.js deterministic.
function stopDelay (stop, nowMinutes) {
  if (!stop) return null
  const promised = promisedEndMinutes(stop.promisedTime)
  if (promised == null) return null
  const reached = D.STATUSES.indexOf(stop.status) >= D.STATUSES.indexOf('arrived')

  if (reached) {
    // The SERVER's stamp decides, not the phone's. driverService keeps
    // `atDevice` only so a wrong phone clock stays visible; letting it drive
    // the delay would have dispatch phoning a shop about a truck that is on
    // time because the driver's phone runs 40 minutes fast. `at` is
    // legitimately absent on the writing client's own optimistic snapshot,
    // before the server confirms -- driverDay's atDevice reading is the
    // fallback for exactly that window, and for nothing else.
    const steps = stop.steps || {}
    const served = minutesFromDate((steps.arrived || {}).at)
    const basis = served == null ? toMinutes(stop.actualArrive) : served
    if (basis == null) return null
    return basis - promised
  }

  // A PREDICTION CANNOT BE IN THE PAST. planStops fixes etaClock at departure
  // plus the predicted leg and never moves it again, so a truck stuck in
  // traffic would keep reporting the lateness it had at the moment it set off
  // -- a green ตรงเวลา on the one truck this board exists to surface. Once the
  // ETA has passed with no arrival stamp, the elapsed time is the honest floor.
  //
  // AND A MISSING PREDICTION IS NOT A MISSING ANSWER. planStops leaves etaClock
  // empty whenever it cannot chain -- no พิกัด and an address geo.js cannot
  // place, which plenty of real rows are. Those stops used to report ไม่ทราบเวลา
  // forever, hiding exactly the ones worth a phone call: the promise ran out at
  // noon, nothing has been stamped, and it is now three o'clock. Not having
  // arrived is itself the evidence, so the clock alone carries the answer.
  const eta = toMinutes(stop.etaClock)
  if (eta == null && nowMinutes == null) return null
  const basis = eta == null
    ? nowMinutes
    : (nowMinutes == null ? eta : Math.max(eta, nowMinutes))
  return basis - promised
}

// The order problems float in. A board is read top-down under time pressure, so
// the truck that needs a phone call is first and the finished ones are last.
const STATE_RANK = { running: 0, notStarted: 1, finished: 2 }
const LEVEL_RANK = { veryLate: 0, late: 1, unknown: 2, onTime: 3 }

function pingFor (doc, now) {
  if (!doc || doc.lat == null || doc.lng == null) return { freshness: 'none' }
  const at = doc.at instanceof Date ? doc.at : null
  const ageMs = at == null ? null : now.getTime() - at.getTime()
  return {
    lat: doc.lat,
    lng: doc.lng,
    accuracy: doc.accuracy == null ? null : doc.accuracy,
    at: at,
    ageMinutes: ageMs == null ? null : Math.max(0, Math.floor(ageMs / 60000)),
    freshness: freshnessFor(ageMs),
    stopId: doc.stopId || '',
    source: doc.source || ''
  }
}

/**
 * @param rows          deliveryList rows for one date, every plate
 * @param stopDocs      { [stopId]: deliveryStop document }
 * @param locationDocs  { [`${date}_${plate}`]: driverLocation document }
 * @param now           a JS Date. Never read from the clock here.
 */
function buildBoard (rows, stopDocs, locationDocs, now, opts) {
  const o = opts || {}
  const keys = o.tripKeys || []
  const tripFor = plate => keys.filter(k => k.regNum === plate)[0] || null
  // With a day, only that day's rows and the rows of a trip still running on it
  // (whose rows carry the departure day). Without one, every row given -- as
  // before trips existed.
  const wanted = r => !o.day || r.deliveryDate === o.day || keys.some(k => k.date === r.deliveryDate && k.regNum === r.regNum)
  const docs = stopDocs || {}
  const locs = locationDocs || {}
  // Read once, here, and handed down. Every row on one board is then measured
  // against the same instant, which a per-stop clock read could not promise.
  const nowMinutes = minutesFromDate(now)

  const byPlate = {}
  ;(rows || []).forEach(r => {
    if (!r) return
    if (!wanted(r)) return
    const plate = r.regNum || ''
    if (!plate) return
    if (!byPlate[plate]) byPlate[plate] = []
    byPlate[plate].push(r)
  })

  const board = Object.keys(byPlate).map(plate => {
    // Group, MERGE, then plan -- the same order driverService.loadDay keeps, and
    // for the same reason: planStops re-anchors the ETA chain on real stamps, so
    // it has to be able to see them. Planning first leaves every ETA chained
    // from the depot at 08:00 all day.
    const stops = D.groupStopsByDate(byPlate[plate])
    let driverName = ''
    let driverCode = ''
    stops.forEach(stop => {
      const saved = docs[stop.id] || {}
      stop.steps = saved.steps || {}
      stop.status = D.statusFor(stop.steps)
      // Photos, signature, rating: what monitor.vue's หลักฐานการส่งงาน dialog
      // shows. Carried as saved -- the board judges nothing about it.
      stop.proof = saved.proof || null
      if (!driverName && saved.driverName) driverName = saved.driverName
      if (!driverCode && saved.driverCode) driverCode = saved.driverCode
    })
    const k = tripFor(plate)
    const plainStops = stops.filter(s => !D.isTripStop(s))
    const tripStops = stops.filter(D.isTripStop)
    D.planStops(plainStops)
    if (tripStops.length) D.planTripDay(tripStops, o.day || (k && k.date) || '')
    const planned = plainStops.concat(tripStops)

    planned.forEach(stop => { stop.delayMinutes = stopDelay(stop, nowMinutes) })

    // A stop the customer rejected is over for the driver just as a delivered
    // one is -- it is not where the truck is going next -- so progress counts
    // closed stops, and the two ways of closing are counted apart for staff.
    const doneCount = planned.filter(s => D.isClosed(s.status)).length
    const failedCount = planned.filter(s => s.status === 'failed').length
    const awaitingCloseCount = planned.filter(D.awaitingClose).length
    const followUpCount = planned.filter(s => s.status === 'done' && s.proof && s.proof.followUp).length
    const current = planned.filter(s => !D.isClosed(s.status))[0] || null
    const started = planned.some(s => s.status !== 'pending')
    const state = doneCount === planned.length && planned.length
      ? 'finished'
      : (started ? 'running' : 'notStarted')

    const delayMinutes = current ? current.delayMinutes : null
    const remaining = planned.filter(s => !D.isClosed(s.status))
    const lateCount = remaining.filter(s =>
      s.delayMinutes != null && s.delayMinutes > ON_TIME_MINUTES).length

    const dateKey = k ? k.date : (planned.length ? planned[0].deliveryDate : '')
    // The one id in this file, built the one way driverService.writePing builds
    // it. Both sides go through safeIdPart so a plate with a '/' in it cannot
    // make the writer and the reader disagree about the document's name.
    const loc = locs[D.safeIdPart(dateKey) + '_' + D.safeIdPart(plate)]

    // The PING wins on identity. ensureStops writes driverName/driverCode onto
    // a stop document only when it is first created -- deliberately, so a
    // relief driver merely opening the day cannot re-attribute another
    // driver's stamped work -- which also means a truck handed over at 11:00
    // still carries the morning driver on every one of its stops. The position
    // document is rewritten continuously by whoever is driving now, so it is
    // the one field that follows the truck, and it is the name dispatch should
    // phone.
    if (loc && loc.driverName) driverName = loc.driverName
    if (loc && loc.driverCode) driverCode = loc.driverCode

    return {
      regNum: plate,
      driverName: driverName,
      driverCode: driverCode,
      state: state,
      doneCount: doneCount,
      failedCount: failedCount,
      followUpCount: followUpCount,
      awaitingCloseCount: awaitingCloseCount,
      totalCount: planned.length,
      current: current,
      stops: planned,
      delayMinutes: delayMinutes,
      delayLevel: delayLevelFor(delayMinutes),
      lateCount: lateCount,
      ping: pingFor(loc, now),
      trip: k ? { dayNo: k.dayNo, days: k.days, overdue: !!k.overdue } : null,
      overdueCount: k && k.overdue ? remaining.length : 0
    }
  })

  board.sort((a, b) => {
    const s = STATE_RANK[a.state] - STATE_RANK[b.state]
    if (s !== 0) return s
    const l = LEVEL_RANK[a.delayLevel] - LEVEL_RANK[b.delayLevel]
    if (l !== 0) return l
    const ad = a.delayMinutes == null ? -Infinity : a.delayMinutes
    const bd = b.delayMinutes == null ? -Infinity : b.delayMinutes
    if (ad !== bd) return bd - ad
    return a.regNum.localeCompare(b.regNum)
  })

  return board
}

module.exports = {
  ON_TIME_MINUTES,
  LATE_MINUTES,
  FRESH_MS,
  STALE_MS,
  toMinutes,
  promisedEndMinutes,
  minutesFromDate,
  delayLevelFor,
  freshnessFor,
  stopDelay,
  buildBoard
}
